// oxlint-disable prefer-default-export, max-lines
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { components } from '@atlaskit/react-select/components';
import AsyncCreatableSelect from '@atlaskit/select/async-creatable-select';
import AsyncSelect from '@atlaskit/select/async-select';
import Select from '@atlaskit/select/default';
import type {
	MenuListComponentProps,
	OptionsType,
	OptionType,
	ValueType,
} from '@atlaskit/select/types';
import TextArea from '@atlaskit/textarea/text-area';

import type { AsyncFieldConfiguration } from '../../../../../../../../common/types/async-fields';
import { OTHER_ID } from '../../../../../../../../common/types/form/form-fields/field-values';
import type { FieldValues } from '../../../../../../../../common/types/form/form-fields/field-values';
import { asOptionsValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import {
	asFormOptionField,
	toOptions,
} from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { CustomSelectOption } from '../../../../../../../../common/ui/custom-select-option';
import {
	getDropDownMenuPosition,
	haveOtherFieldValuesChanged,
} from '../../../../../../../../common/utils';
import {
	useCurrentValue,
	useCurrentValues,
	useFieldInvalid,
	useFieldRequired,
	useFormField,
	useFormRendererActions,
	useFormRendererState,
} from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';
import { PaginatedEditMultipleSelectInput } from './paginated-edit-multiple-select-input';

const LOAD_OPTIONS_DEBOUNCE_WAIT_MS = 500;
const optionStyles = xcss({ marginTop: 'space.050' });

export const EditMultipleSelectInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(
		useCurrentValue,
		useCurrentValues,
		useFormField,
		useFormRendererActions,
		useFormRendererState,
		debounce,
	);
	const intl = useIntl();

	const currentValue = asOptionsValue(useCurrentValue({ fieldId }));
	const currentValues = useCurrentValues();
	const formField = useFormField({ fieldId });
	const formOptionField = asFormOptionField(formField);
	const { asyncFields } = useFormRendererState();
	const fieldAsyncConfiguration = asyncFields[fieldId];
	const { updateModifiedValue } = useFormRendererActions();
	const isInvalid = useFieldInvalid({ fieldId });
	const isRequired = useFieldRequired({ fieldId });
	const [otherTextInputValue, setOtherTextInputValue] = useState(currentValue?.otherOptionText);
	// Label fields remount the select to reload the full list. Keep the query here so that
	// remount still shows what the user typed.
	const [labelSearchText, setLabelSearchText] = useState('');
	const handleLabelSearchChange = useCallback((value: string) => {
		setLabelSearchText(value);
		return value;
	}, []);
	const [loadOptionsOnFocusDefaultOptions, setLoadOptionsOnFocusDefaultOptions] =
		useState<OptionsType>();
	const [isLoadingOptionsOnFocus, setIsLoadingOptionsOnFocus] = useState(false);
	// Tracks the `currentValues` and async config used for the last focus load so we can skip a
	// refetch when nothing that could make this field's options stale has changed. `undefined`
	// means the focus options have never been loaded for this field.
	const lastLoadedValuesRef = useRef<FieldValues | undefined>(undefined);
	const lastLoadedConfigRef = useRef<AsyncFieldConfiguration | undefined>(undefined);
	// Monotonic counter used to discard responses from superseded in-flight requests. Each fetch
	// increments the counter; a response is applied only when its captured id still matches.
	const loadRequestCounterRef = useRef(0);

	useEffect(() => {
		setOtherTextInputValue(currentValue?.otherOptionText);
	}, [currentValue?.otherOptionText, setOtherTextInputValue]);

	const handleLoadOptionsOnFocus = useCallback(() => {
		if (!fieldAsyncConfiguration?.loadOptions) {
			return;
		}

		const previousValues = lastLoadedValuesRef.current;
		// Only (re)load on the first focus, when the async config changes, or when a field value
		// OTHER than this field's own changes. Changing this field's own selection (e.g. removing a
		// chip) cannot make its own option list stale, so we skip the refetch (avoiding an
		// unnecessary network round-trip on every reopen) and keep the already-loaded options so
		// that de-selected values reappear in the menu client-side.
		const shouldLoad =
			previousValues === undefined ||
			lastLoadedConfigRef.current !== fieldAsyncConfiguration ||
			haveOtherFieldValuesChanged(previousValues, currentValues, fieldId);

		if (!shouldLoad) {
			return;
		}

		lastLoadedValuesRef.current = currentValues;
		lastLoadedConfigRef.current = fieldAsyncConfiguration;
		loadRequestCounterRef.current += 1;
		const requestId = loadRequestCounterRef.current;
		setIsLoadingOptionsOnFocus(true);
		fieldAsyncConfiguration
			.loadOptions('', currentValues, fieldId)
			// loadRequestCounterRef check early returns are stale guards
			.then((options: OptionsType) => {
				if (requestId !== loadRequestCounterRef.current) {
					return;
				}
				setLoadOptionsOnFocusDefaultOptions(options);
			})
			.catch(() => {
				if (requestId !== loadRequestCounterRef.current) {
					return;
				}
				setLoadOptionsOnFocusDefaultOptions([]);
			})
			.finally(() => {
				if (requestId !== loadRequestCounterRef.current) {
					return;
				}
				setIsLoadingOptionsOnFocus(false);
			});
	}, [currentValues, fieldAsyncConfiguration, fieldId]);

	const onChange = useCallback(
		(value: ValueType<OptionType, true>): void => {
			const options = value.map((option) => ({ id: option.value.toString(), label: option.label }));
			if (options.length === 0) {
				updateModifiedValue(fieldId, null);
			} else if (options.some((option) => option.id === OTHER_ID)) {
				updateModifiedValue(fieldId, {
					otherOptionText: currentValue?.otherOptionText ?? '',
					options: options.filter((option) => option.id !== OTHER_ID),
				});
			} else {
				updateModifiedValue(fieldId, { options });
			}
		},
		[currentValue?.otherOptionText, fieldId, updateModifiedValue],
	);

	const debouncedUpdateModifiedValue = useMemo(
		() => debounce(updateModifiedValue, 500),
		[updateModifiedValue],
	);

	const menuFooter = fieldAsyncConfiguration?.menuFooter;
	const asyncSelectComponents = useMemo(() => {
		if (menuFooter === undefined) {
			return { Option: CustomSelectOption<true> };
		}
		return {
			Option: CustomSelectOption<true>,
			MenuList: (menuListProps: MenuListComponentProps<OptionType, true>) => (
				<>
					<components.MenuList
						{...(menuListProps as React.ComponentProps<typeof components.MenuList>)}
					/>
					{menuFooter}
				</>
			),
		};
	}, [menuFooter]);

	const onOtherOptionTextChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>): void => {
			setOtherTextInputValue(event.target.value);
			debouncedUpdateModifiedValue(fieldId, {
				otherOptionText: event.target.value,
				options: currentValue?.options ?? [],
			});
		},
		[currentValue?.options, debouncedUpdateModifiedValue, fieldId, setOtherTextInputValue],
	);

	if (formOptionField === undefined) {
		return <></>;
	}

	const otherOption = { value: OTHER_ID, label: intl.formatMessage(messages.otherOption) };

	if (fieldAsyncConfiguration === undefined) {
		return (
			<Box testId={`field-input-multiple-select-edit-${fieldId}`}>
				<Select<OptionType, true>
					inputId={fieldInputId}
					options={
						formOptionField.hasOtherOption
							? [...toOptions(formOptionField.options), otherOption]
							: toOptions(formOptionField.options)
					}
					value={
						currentValue?.options?.length
							? toOptions(currentValue?.options).concat(
									currentValue?.otherOptionText !== undefined ? [otherOption] : [],
								)
							: currentValue?.otherOptionText !== undefined
								? [otherOption]
								: null
					}
					isMulti
					onChange={onChange}
					isSearchable
					noOptionsMessage={() => <FormattedMessage {...messages.optionDropdownNoOptions} />}
					placeholder={<FormattedMessage {...messages.optionDropdownSelect} />}
					{...getDropDownMenuPosition()}
					isInvalid={isInvalid}
					testId={`field-input-multiple-select-edit-input-${fieldId}`}
					isRequired={isRequired}
					descriptionId={
						isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
					}
					components={{ Option: CustomSelectOption<true> }}
				/>
				{otherTextInputValue !== undefined && (
					<Box xcss={optionStyles}>
						<TextArea
							appearance="standard"
							placeholder={intl.formatMessage(messages.optionDropdownNewOption)}
							minimumRows={2}
							isMonospaced={false}
							value={otherTextInputValue}
							resize="smart"
							spellCheck={false}
							onChange={onOtherOptionTextChange}
							isInvalid={isInvalid}
							testId={`field-input-multiple-select-edit-other-${fieldId}`}
							aria-label={intl.formatMessage(messages.otherOptionTextLabel, {
								label: formField.label,
							})}
							aria-describedby={
								isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
							}
						/>
					</Box>
				)}
			</Box>
		);
	}
	if (fieldAsyncConfiguration.loadPaginatedOptions) {
		return (
			<PaginatedEditMultipleSelectInput
				fieldId={fieldId}
				fieldInputId={fieldInputId}
				fieldErrorId={fieldErrorId}
				fieldAsyncConfiguration={fieldAsyncConfiguration}
				currentValue={currentValue}
				currentValues={currentValues}
				isInvalid={isInvalid}
				isRequired={isRequired}
				onChange={onChange}
			/>
		);
	}
	const loadOptions = debounce((query: string, callback: (options: OptionsType) => void) => {
		fieldAsyncConfiguration
			.loadOptions?.(query, currentValues, fieldId)
			.then((options: OptionsType) => {
				const heading = fieldAsyncConfiguration.optionsHeading;
				// react-select treats an entry with `options` as a group heading.
				callback(
					// oxlint-disable-next-line typescript/consistent-type-assertions
					heading ? ([{ label: heading, options }] as unknown as OptionsType) : options,
				);
			})
			.catch(() => callback([]));
	}, LOAD_OPTIONS_DEBOUNCE_WAIT_MS);

	const { createOptions } = fieldAsyncConfiguration;
	const keepsSearchAcrossReload = fieldAsyncConfiguration.defaultMenuIsOpen !== undefined;
	const labelSearchProps = keepsSearchAcrossReload
		? {
				inputValue: labelSearchText,
				onInputChange: handleLabelSearchChange,
				autoFocus: fieldAsyncConfiguration.defaultMenuIsOpen,
			}
		: {};
	const { isValidNewOption, onCreateOption } = createOptions || {};
	const isCreateable: boolean = createOptions !== undefined;

	const getLoadOptionsOnEventBehavior = () => {
		const isDefaultOptionsConfigurationProvided =
			fieldAsyncConfiguration.defaultOptions !== undefined;
		// AsyncFieldConfiguration.defaultOptions is now not a direct mapping to the prop behavior of react-select.
		// In react-select the defaultOptions prop being undefined or explicitly false are functionally the same, just
		// like any other React component. However here, we define defaultOptions as part of a store hook for a config
		// object. Here we use the config of defaultOptions to have different behavior for `undefined`, i.e. the `onFocus`
		// triggering of `loadOptions`
		const defaultOptionsProps = {
			defaultOptions: fieldAsyncConfiguration.defaultOptions,
		};
		const loadOptionsOnFocusProps = {
			onFocus: handleLoadOptionsOnFocus,
			onMenuOpen: handleLoadOptionsOnFocus,
			openMenuOnFocus: true,
			defaultOptions: loadOptionsOnFocusDefaultOptions,
			isLoading: isLoadingOptionsOnFocus,
		};

		return isDefaultOptionsConfigurationProvided ? defaultOptionsProps : loadOptionsOnFocusProps;
	};

	return (
		<Box testId={`field-input-multiple-select-edit-${fieldId}`}>
			{isCreateable ? (
				<Box testId={`field-input-multiple-select-edit-async-createable-${fieldId}`}>
					<AsyncCreatableSelect<OptionType, true>
						key={fieldAsyncConfiguration.selectKey}
						defaultMenuIsOpen={fieldAsyncConfiguration.defaultMenuIsOpen}
						{...labelSearchProps}
						inputId={fieldInputId}
						{...getLoadOptionsOnEventBehavior()}
						isSearchable
						noOptionsMessage={
							fieldAsyncConfiguration.noOptionsMessage ??
							(() => <FormattedMessage {...messages.optionDropdownNoOptions} />)
						}
						loadOptions={loadOptions}
						onCreateOption={onCreateOption}
						isValidNewOption={isValidNewOption}
						{...getDropDownMenuPosition()}
						onChange={onChange}
						placeholder={<FormattedMessage {...messages.optionDropdownSelect} />}
						value={toOptions(currentValue?.options) ?? null}
						isMulti
						isInvalid={isInvalid}
						testId={`field-input-multiple-select-edit-input-${fieldId}`}
						isRequired={isRequired}
						components={asyncSelectComponents}
						cacheOptions={fieldAsyncConfiguration.cacheOptions}
						descriptionId={
							isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
						}
					/>
				</Box>
			) : (
				<Box testId={`field-input-multiple-select-edit-async-${fieldId}`}>
					<AsyncSelect<OptionType, true>
						key={fieldAsyncConfiguration.selectKey}
						defaultMenuIsOpen={fieldAsyncConfiguration.defaultMenuIsOpen}
						{...labelSearchProps}
						inputId={fieldInputId}
						{...getLoadOptionsOnEventBehavior()}
						isSearchable
						noOptionsMessage={
							fieldAsyncConfiguration.noOptionsMessage ??
							(() => <FormattedMessage {...messages.optionDropdownNoOptions} />)
						}
						loadOptions={loadOptions}
						{...getDropDownMenuPosition()}
						onChange={onChange}
						placeholder={<FormattedMessage {...messages.optionDropdownSelect} />}
						value={toOptions(currentValue?.options) ?? null}
						isMulti
						isInvalid={isInvalid}
						testId={`field-input-multiple-select-edit-input-${fieldId}`}
						isRequired={isRequired}
						components={asyncSelectComponents}
						cacheOptions={fieldAsyncConfiguration.cacheOptions}
						descriptionId={
							isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
						}
					/>
				</Box>
			)}
		</Box>
	);
};
